import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import User2 from "../img/User2.svg";
import User1 from "../img/User1.svg";
import RightPurple from "../img/RightPurple.svg";
import Setting_line from "../img/Setting_line.svg";
import SettingLog from "../img/SettingLog.svg";
import Add_round2 from "../img/SettingLog.svg";
import ProfileLog from "../img/profileLog.svg";
import Expand_down from "../img/Expand_down.svg";
import ProfilePlus from "../img/ProfilePlus.svg";
import BackgroundImg from "../img/BackgroundNetImg.svg";
import { useNavigate } from "react-router-dom";
import api from "../../api/axiosInstance";
import CustomLoader from "./CustomLoader";
import { motion, AnimatePresence } from "framer-motion";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [showSizeErrorModal, setShowSizeErrorModal] = useState(false);
  const [selected, setSelected] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const [passwordLastChanged, setPasswordLastChanged] = useState("");

  const [profileData, setProfileData] = useState(null);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [apiMessage, setApiMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("accessToken"); // adjust if stored differently
        const response = await api.get("/admin-app/api/v1/read-profile", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setProfileData(response.data.data); // adjust `data` path as per your API response

        const passwordChangeTime = calculatePasswordChangeTime(
          response.data.data?.user_details?.last_password_change_date
        );
        setPasswordLastChanged(passwordChangeTime);
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };

    fetchProfile(); // ✅ call the function here
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };

    if (sidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen]);

  const handleInputChange = (e, field) => {
    const { value } = e.target;
    const fields = field.split(".");
    setProfileData((prevData) => {
      const updatedData = { ...prevData };
      let current = updatedData;

      fields.forEach((part, index) => {
        if (index === fields.length - 1) {
          current[part] = value;
        } else {
          if (!current[part]) current[part] = {};
          current = current[part];
        }
      });

      return updatedData;
    });
  };

  const onProfilePicChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePicFile(file);
      setProfilePicPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    const name = profileData?.name?.trim();
    const email = profileData?.user_details?.email?.trim();
    const phone = profileData?.user_details?.phone_number?.trim();
    const firstName = profileData?.user_details?.first_name?.trim();

    if (!firstName) {
      setApiMessage("Name is required.");
      setShowErrorModal(true);
      return;
    }

    if (!name) {
      setApiMessage("Company Name is required.");
      setShowErrorModal(true);
      return;
    }

    if (!email) {
      setApiMessage("Email Address is required.");
      setShowErrorModal(true);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setApiMessage("Please enter a valid email address.");
      setShowErrorModal(true);
      return;
    }

    if (!phone) {
      setApiMessage("Mobile number is required.");
      setShowErrorModal(true);
      return;
    }
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phone)) {
      setApiMessage("Mobile number must be exactly 10 digits.");
      setShowErrorModal(true);
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");
      const fd = new FormData();

      // file: append only if a new file was chosen
      if (profilePicFile) {
        fd.append("profile_pic", profilePicFile, profilePicFile.name);
      }

      // flat fields
      fd.append("first_name", profileData?.user_details?.first_name ?? "");
      fd.append("name", profileData?.name ?? "");
      fd.append("email", profileData?.user_details?.email ?? "");
      fd.append("phone_number", profileData?.user_details?.phone_number ?? "");
      fd.append("cin_number", profileData?.cin_number ?? "");
      fd.append("invoice_suffix", profileData?.invoice_suffix ?? "");
      fd.append("gst_number", profileData?.gst_number ?? "");
      fd.append("pan_number", profileData?.pan_number ?? "");
      fd.append("sac_number", profileData?.sac_number ?? "");
      fd.append(
        "is_payment_gateway_available",
        profileData?.is_payment_gateway_available ? "true" : "false"
      );

      // nested object as JSON (typical for DRF)
      fd.append(
        "gateway_details",
        JSON.stringify({
          key_id: profileData?.gateway_details?.key_id ?? "",
          secret_key: profileData?.gateway_details?.secret_key ?? "",
          webhook_key: profileData?.gateway_details?.webhook_key ?? "",
        })
      );

      const response = await api.patch("/admin-app/api/v1/edit-profile", fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "success") {
        setApiMessage(response.data.message || "License created successfully!");
        setShowSuccessModal(true);
      } else {
        setApiMessage(
          response.data.message || "Error during license creation."
        );
        setShowErrorModal(true);
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving profile data:", error);
    }
  };

  const calculatePasswordChangeTime = (lastPasswordChangeDate) => {
  if (!lastPasswordChangeDate) return "Not changed yet";

  const now = new Date();
  const lastChange = new Date(lastPasswordChangeDate);
  const diffTime = Math.abs(now - lastChange);
  
  // Calculate all time units
  const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
  const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30));
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

  if (diffYears > 0) {
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  } else if (diffMonths > 0) {
    return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  } else if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  } else {
    return "Just now";
  }
};

  if (!profileData) {
    return <CustomLoader></CustomLoader>; // Loading state while waiting for the data
  }

  return (
    <div>
      <Header></Header>
      {/* Mobile Top Bar with Hamburger */}
      <div className="md:hidden flex justify-between items-center px-4 py-3 bg-white shadow">
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-3xl"
          aria-label="Open sidebar"
        >
          &#9776;
        </button>
      </div>

      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full w-1/2 bg-white shadow-lg z-50 py-4 overflow-y-auto transition-all duration-500 ease-in-out ${
          sidebarOpen
            ? "translate-x-0 opacity-100"
            : "-translate-x-full opacity-0"
        }`}
      >
        <div className="flex justify-between items-center px-4 py-3 border-b">
          <h2 className="text-lg font-semibold">Menu</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-2xl font-bold"
          >
            &times;
          </button>
        </div>

        <div className="flex flex-col gap-4 px-4 py-6">
          <div
            className={`flex items-center gap-3 cursor-pointer p-2 rounded ${
              activeTab === "profile" ? "bg-[#F7F2FF]" : ""
            }`}
            onClick={() => {
              setActiveTab("profile");
              setSidebarOpen(false);
            }}
          >
            <img src={activeTab === "profile" ? User2 : User1} alt="Profile" />
            <span
              className={`${
                activeTab === "profile" ? "text-[#9061DB]" : "text-black"
              }`}
            >
              Profile
            </span>
          </div>

          <div
            className={`flex items-center gap-3 cursor-pointer p-2 rounded ${
              activeTab === "settings" ? "bg-[#F7F2FF]" : ""
            }`}
            onClick={() => {
              setActiveTab("settings");
              navigate("/settings", {
                state: {
                  passwordLastChanged: passwordLastChanged,
                },
              });
              setSidebarOpen(false);
            }}
          >
            <img
              src={activeTab === "settings" ? SettingLog : Setting_line}
              alt="Settings"
            />
            <span
              className={`${
                activeTab === "settings" ? "text-[#9061DB]" : "text-black"
              }`}
            >
              Settings
            </span>
          </div>
        </div>
      </div>

      <div
        className="flex  flex-col md:flex-row px-4 md:px-10 py-6 w-full fixed inzet-0 z-0 bg-white bg-cover bg-center"
        style={{
          backgroundImage: `url(${BackgroundImg})`,
          backgroundPosition: "50% 0%",
        }}
      >
        {/* Sidebar */}
        <div className="md:flex hidden gap-2 flex-col w-full md:w-2/12 mb-6 md:mb-0 font-semibold">
          {/* Profile Tab */}
          <div
            className="flex items-center gap-3 cursor-pointer h-9 rounded"
            onClick={() => setActiveTab("profile")}
          >
            <div
              className="w-1 h-5 rounded"
              style={{
                backgroundColor:
                  activeTab === "profile" ? "#9061DB" : "transparent",
              }}
            ></div>
            <img
              src={activeTab === "profile" ? User2 : User1}
              alt="Profile Icon"
            />
            <span
              style={{ color: activeTab === "profile" ? "#9061DB" : "black" }}
            >
              Profile
            </span>
            {activeTab === "profile" && <img src={RightPurple} alt="arrow" />}
          </div>

          {/* Settings Tab */}
          {/* <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setActiveTab("settings")}
          > */}

          <div
            className={`flex items-center gap-3 cursor-pointer h-9 rounded hover:bg-[#F7F2FF] ${
              activeTab !== "settings" ? "" : ""
            }`}
            onClick={() => {
              setActiveTab("settings");
              navigate("/settings", {
                state: {
                  passwordLastChanged: passwordLastChanged,
                },
              });
              setSidebarOpen(false);
            }}
          >
            <div
              className="w-1 h-5 rounded"
              style={{
                backgroundColor:
                  activeTab === "settings" ? "#9061DB" : "transparent",
              }}
            ></div>

            <img
              src={activeTab === "settings" ? SettingLog : Setting_line}
              alt="Profile Icon"
            />
            <span
              style={{ color: activeTab === "settings" ? "#9061DB" : "black" }}
            >
              Settings
            </span>
            {activeTab === "settings" && <img src={RightPurple} alt="arrow" />}
          </div>
        </div>

        {/* Separator Line */}
        <div className="hidden md:block w-px bg-gray-300 h-screen" />

        {/* Content */}
        <div className="px-4 md:px-10  w-full md:w-10/12 overflow-y-auto max-h-[calc(100vh-220px)] md:max-h-[calc(100vh-64px)] scrollbar-hide">
          <h1 className="text-2xl font-semibold">Profile Details</h1>

          {/* Display Container */}
          {!isEditing && (
            <div className="py-5 rounded my-5">
              <div className="px-5">
                {/* Header: Profile Pic + Name + Edit */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  {/* Profile Picture and Info */}
                  <div className="flex gap-4 items-center">
                    <img
                      src={
                        profilePicPreview ||
                        profileData?.user_details?.profile_pic ||
                        ProfileLog
                      } // If no profile pic, use the default ProfileLog
                      alt="Profile"
                      className="h-20 w-20 rounded-full object-cover border border-gray-300 shadow"
                    />
                    <div className="flex flex-col">
                      <span className="text-xl sm:text-2xl font-semibold"></span>
                      {profileData?.user_details.first_name || "Name"}

                      <span className="text-sm sm:text-base text-gray-700 max-w-[350px]  break-words">
                        {profileData?.user_details.email || "Email"}
                      </span>
                    </div>
                  </div>

                  {/* Edit Button */}
                  <button
                    className="font-bold text-sm h-9 w-full sm:w-24 text-white rounded bg-primary hover:scale-105 transition-transform duration-300"
                    style={{
                      // background:
                      //   "linear-gradient(270deg, #9061DB 0%, #8777F3 100%)",
                      willChange: "transform",
                    }}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </button>
                </div>

                {/* Info Grid */}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-3">
                  {/* <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-600">
                      Company Domain
                    </span>
                    <span className="text-base font-medium">
                      https://name.in/portal/
                    </span>
                  </div> */}
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">Mobile Number</span>
                    <span className="text-base font-medium">
                      {profileData?.user_details.phone_number || "Mobile"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600"> Company Name</span>
                    <span className="text-base font-medium">
                      {" "}
                      {profileData?.name || "Organization Name"}
                    </span>
                  </div>
                </div>
                <div className="text-lg font-semibold mt-5 mb-2">
                  <span className="text-2xl">Tax Details</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">CIN Number</span>
                    <span
                      className={`text-base font-medium ${
                        profileData?.cin_number
                          ? "text-[black]"
                          : "text-gray-600"
                      }`}
                    >
                      {profileData?.cin_number || "-----"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">
                      Invoice Suffix
                    </span>
                    <span
                      className={`text-base font-medium ${
                        profileData?.invoice_suffix
                          ? "text-[black]"
                          : "text-gray-600"
                      }`}
                    >
                      {profileData?.invoice_suffix || "-----"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">GST Number</span>
                    <span
                      className={`text-base font-medium ${
                        profileData?.gst_number
                          ? "text-[black]"
                          : "text-gray-600"
                      }`}
                    >
                      {" "}
                      {profileData?.gst_number || "-----"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">PAN Number</span>
                    <span
                      className={`text-base font-medium ${
                        profileData?.pan_number
                          ? "text-[black]"
                          : "text-gray-600"
                      }`}
                    >
                      {" "}
                      {profileData?.pan_number || "-----"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">SAC Number</span>
                    <span
                      className={`text-base font-medium ${
                        profileData?.sac_number
                          ? "text-[black]"
                          : "text-gray-600"
                      }`}
                    >
                      {profileData?.sac_number || "-----"}
                    </span>
                  </div>
                </div>

                <div className="text-lg font-semibold mt-5 mb-2">
                  <span className="text-2xl">Payment Gateway Details</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">
                      Payment Gateway Status
                    </span>
                    <span className="text-base font-medium">
                      {profileData?.is_payment_gateway_available ? "Yes" : "No"}
                    </span>
                  </div>

                  {/* Conditionally render the following fields if the payment gateway is available */}
                  {profileData?.is_payment_gateway_available && (
                    <>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-gray-600">Key Id</span>
                        <span className="text-base font-medium">
                          {profileData?.gateway_details?.key_id || "key_id"}
                        </span>
                      </div>

                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-gray-600">
                          Secret Key
                        </span>
                        <span className="text-base font-medium">
                          {profileData?.gateway_details?.secret_key ||
                            "secret_key"}
                        </span>
                      </div>

                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-gray-600">
                          Webhook Key
                        </span>
                        <span className="text-base font-medium">
                          {profileData?.gateway_details?.webhook_key ||
                            "webhook_key"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Edit Container */}
          {isEditing && (
            <div className="py-5 rounded my-5">
              <div className="px-5">
                <div className="flex justify-between items-start md:items-center gap-4">
                  <div className="flex gap-2">
                    <div className=" relative">
                      {/* Profile Picture Preview */}
                      <img
                        src={
                          profilePicPreview ||
                          profileData?.user_details?.profile_pic ||
                          ProfileLog
                        }
                        alt="Profile"
                        className="h-20 w-20 rounded-full object-cover border border-gray-300"
                      />

                      {/* Hidden file input with label trigger */}
                      <label className="cursor-pointer text-sm text-blue-600 hover:underline absolute bottom-[1px] right-0 w-5 h-5 border border-4 border-[white] rounded-full">
                        <img src={ProfilePlus} alt="Upload" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={onProfilePicChange} // Handle file change for profile picture
                        />
                      </label>
                    </div>
                  </div>

                  <button
                    className="font-bold text-sm h-9 w-24 text-white rounded hover:scale-105 transition-transform duration-300 bg-primary"
                    onClick={handleSave}
                    style={{
                      // background:
                      //   "linear-gradient(270deg, #9061DB 0%, #8777F3 100%)",
                      willChange: "transform",
                    }}
                  >
                    Save
                  </button>
                </div>

                <div className="flex flex-col mt-5 w-full gap-5">
                  {/* Name and Org */}
                  <div className="flex flex-col gap-4 w-full">
                    <div className="flex flex-col gap-1">
                      <label className="text-gray-600 text-sm">
                        Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="Name"
                        maxLength={25}
                        value={profileData?.user_details?.first_name || ""}
                        onChange={(e) =>
                          handleInputChange(e, "user_details.first_name")
                        }
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-gray-600 text-sm">
                        Company Name
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="Company Name"
                        maxLength={25}
                        value={profileData?.name || ""}
                        onChange={(e) => handleInputChange(e, "name")}
                      />
                    </div>
                  </div>

                  {/* Email & Mobile */}
                  <div className="flex gap-4 w-full">
                    <div className="flex flex-col gap-1 w-1/2">
                      <label className="text-gray-600 text-sm">
                        Email Address
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="Email Address"
                        maxLength={25}
                        value={profileData?.user_details?.email || ""}
                        onChange={(e) =>
                          handleInputChange(e, "user_details.email")
                        }
                      />
                    </div>

                    <div className="flex flex-col gap-1 w-1/2">
                      <label className="text-gray-600 text-sm">
                        Mobile Number
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="Mobile Number"
                        value={profileData?.user_details?.phone_number || ""}
                        maxLength={10}
                        onChange={(e) => {
                          const onlynums = e.target.value.replace(/\D/g, "");
                          handleInputChange(
                            { target: { value: onlynums } },
                            "user_details.phone_number"
                          );
                        }}
                      />
                    </div>
                  </div>

                  {/* Tax Details */}
                  <div className="my-1">
                    <span className="text-2xl font-semibold">Tax Details</span>
                  </div>

                  <div className="flex gap-4 w-full">
                    <div className="flex flex-col gap-1 w-1/2">
                      <label className="text-gray-600 text-sm">
                        CIN Number
                      </label>
                      <input
                        type="text"
                        className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="CIN Number"
                        maxLength={21}
                        value={profileData?.cin_number || ""}
                        onChange={(e) => handleInputChange(e, "cin_number")}
                      />
                    </div>

                    <div className="flex flex-col gap-1 w-1/2">
                      <label className="text-gray-600 text-sm">
                        Invoice Suffix
                      </label>
                      <input
                        type="text"
                        className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="Invoice Suffix"
                        maxLength={10}
                        value={profileData?.invoice_suffix || ""}
                        onChange={(e) => handleInputChange(e, "invoice_suffix")}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 w-full">
                    <div className="flex flex-col gap-1 w-1/2">
                      <label className="text-gray-600 text-sm">
                        GST Number
                      </label>
                      <input
                        type="text"
                        className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="GST Number"
                        maxLength={15}
                        value={profileData?.gst_number || ""}
                        onChange={(e) => handleInputChange(e, "gst_number")}
                      />
                    </div>

                    <div className="flex flex-col gap-1 w-1/2">
                      <label className="text-gray-600 text-sm">
                        PAN Number
                      </label>
                      <input
                        type="text"
                        className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="PAN Number"
                        maxLength={10}
                        value={profileData?.pan_number || ""}
                        onChange={(e) => handleInputChange(e, "pan_number")}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 w-full">
                    <div className="flex flex-col gap-1 w-1/2">
                      <label className="text-gray-600 text-sm">
                        SAC Number
                      </label>
                      <input
                        type="text"
                        className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        placeholder="SAC Number"
                        maxLength={6}
                        value={profileData?.sac_number || ""}
                        onChange={(e) => handleInputChange(e, "sac_number")}
                      />
                    </div>
                  </div>

                  {/* Payment Gateway */}
                  {/* Payment Gateway */}
                  <div className="my-1">
                    <span className="text-2xl font-semibold">
                      Payment Gateway Details
                    </span>
                  </div>

                  <div className="flex gap-4 w-full">
                    <div className="flex flex-col gap-1 w-1/2">
                      <label className="text-gray-600 text-sm">
                        
                        Do you have a payment gateway available?
                      </label>
                      <select
                        className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                        value={
                          profileData?.is_payment_gateway_available
                            ? "Yes"
                            : "No"
                        }
                        onChange={(e) =>
                          setProfileData((prev) => ({
                            ...prev,
                            is_payment_gateway_available:
                              e.target.value === "Yes",
                          }))
                        }
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>

                  {/* Conditionally show gateway fields */}
                  {profileData?.is_payment_gateway_available && (
                    <>
                      <div className="flex gap-4 w-full mt-4">
                        <div className="flex flex-col gap-1 w-1/2">
                          <label className="text-gray-600 text-sm">
                            Key ID
                          </label>
                          <input
                            type="text"
                            className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                            placeholder="Key ID"
                            value={profileData?.gateway_details?.key_id || ""}
                            onChange={(e) =>
                              handleInputChange(e, "gateway_details.key_id")
                            }
                          />
                        </div>
                      </div>

                      <div className="flex gap-4 w-full">
                        <div className="flex flex-col gap-1 w-1/2">
                          <label className="text-gray-600 text-sm">
                            Secret Key
                          </label>
                          <input
                            type="text"
                            className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                            placeholder="Secret Key"
                            value={
                              profileData?.gateway_details?.secret_key || ""
                            }
                            onChange={(e) =>
                              handleInputChange(e, "gateway_details.secret_key")
                            }
                          />
                        </div>

                        <div className="flex flex-col gap-1 w-1/2">
                          <label className="text-gray-600 text-sm">
                            Webhook Key
                          </label>
                          <input
                            type="text"
                            className="pl-5 h-10 pr-4 border-2 border-[#8C8C8C4D] bg-transparent rounded-md font-inter focus:outline-none"
                            placeholder="Webhook Key"
                            value={
                              profileData?.gateway_details?.webhook_key || ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                e,
                                "gateway_details.webhook_key"
                              )
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showSizeErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-semibold mb-4 text-center">
              File Upload Error
            </h2>
            <p className="text-sm text-gray-700 text-center"></p>
            <div className="mt-6 flex justify-center">
              <button
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded"
                onClick={() => setShowSizeErrorModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showSuccessModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                className="w-full max-w-md bg-white rounded-xl shadow-xl"
              >
                <div className="flex justify-center pt-8">
                  <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50">
                    <motion.div
                      className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50"
                      animate={{
                        rotate: [0, -10, 10, -10, 10, 0], // shake motion
                      }}
                      transition={{
                        duration: 0.6,
                        ease: "easeInOut",
                      }}
                    >
                      <svg
                        className="w-12 h-12 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </motion.div>
                  </div>
                </div>
                <div className="px-6 py-4 text-center">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    Success!
                  </h3>
                  <p className="text-gray-600 mb-6">{apiMessage}</p>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setShowSuccessModal(false)}
                    className="w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg"
                  >
                    Continue
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showErrorModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                className="w-full max-w-md bg-white rounded-xl shadow-xl"
              >
                <div className="flex justify-center pt-8">
                  <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50">
                    <motion.div
                      className="flex items-center justify-center w-20 h-20 rounded-full bg-green-50"
                      animate={{
                        rotate: [0, -10, 10, -10, 10, 0], // shake motion
                      }}
                      transition={{
                        duration: 0.6,
                        ease: "easeInOut",
                      }}
                    >
                      <svg
                        className="w-8 h-8 text-red-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeWidth={2}
                          strokeLinecap="round"
                          d="M12 8v4m0 4h.01M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"
                        />
                      </svg>
                    </motion.div>
                  </div>
                </div>
                <div className="px-6 py-4 text-center">
                  <p className="text-gray-600 mb-6">{apiMessage}</p>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setShowErrorModal(false)}
                    className="w-full py-3 px-4 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg"
                  >
                    Try Again
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfilePage;
